import { useCallback, useRef, useState, useEffect } from 'react';
import type { Config, PopoverDOM } from 'driver.js';
import { navigate, navigationRef, isWeb, setSecureValue } from '@helpers';
import { waitForElement, saveProgress, loadProgress, clearProgress, delay } from '@components/tours/helpers';
import { setTourActive } from '@components/tours/helpers/demoDataRegistry';
import { globalTourConfigs } from '@components/tours/config';
import { DEFAULT_DRIVER_CONFIG } from '@config/driver';
import type {
  GlobalTourPhase,
  GlobalTourProgress,
  GlobalTourConfig,
  TourSegment,
  TourConfig,
} from '../types';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

interface UseGlobalTourConductorParams {
  startLocalTour: (config: TourConfig, onDestroyed?: (closedByUser: boolean) => void) => void;
  stopLocalTour: () => void;
  isLocalTourActive: boolean;
}

interface UseGlobalTourConductorReturn {
  startGlobalTour: (tourId: string) => Promise<void>;
  stopGlobalTour: () => void;
  isGlobalTourActive: boolean;
  globalTourProgress: GlobalTourProgress | null;
  globalTourPhase: GlobalTourPhase;
  currentSegmentIndex: number;
  totalSegments: number;
}

export const useGlobalTourConductor = ({
  startLocalTour,
  stopLocalTour,
  isLocalTourActive,
}: UseGlobalTourConductorParams): UseGlobalTourConductorReturn => {
  const dispatch = useAppDispatch();
  const [phase, setPhase] = useState<GlobalTourPhase>('idle');
  const [progress, setProgress] = useState<GlobalTourProgress | null>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const currentSegmentIndexRef = useRef(0);
  const [totalSegments, setTotalSegments] = useState(0);

  const currentTourConfigRef = useRef<GlobalTourConfig | null>(null);
  const currentSegmentRef = useRef<TourSegment | null>(null);
  const isAbortingRef = useRef(false);

  const isGlobalTourActive = phase !== 'idle' && phase !== 'completed' && phase !== 'aborted';

  // Cleanup current segment's demo data
  const cleanupCurrentSegment = useCallback(async () => {
    if (currentSegmentRef.current?.demoData?.cleanup) {
      try {
        await dispatch(currentSegmentRef.current.demoData.cleanup());
      } catch (error) {
        console.warn('Failed to cleanup segment demo data:', error);
      }
    }
  }, [dispatch]);

  // Stop the global tour completely
  const stopGlobalTour = useCallback(async () => {
    if (isAbortingRef.current) return;
    isAbortingRef.current = true;

    // Stop any running local tour first
    stopLocalTour();

    // Cleanup current segment
    await cleanupCurrentSegment();

    // Clear persistence + unblock API calls
    clearProgress();
    setTourActive(false);

    // Track tour abandoned — use refs instead of state (state may not be updated yet)
    const tourSegments = currentTourConfigRef.current?.segments;
    trackEvent(TRACKING_EVENTS.WELCOME_TOUR, {
      action: 'abandoned',
      lastSegmentName: currentSegmentRef.current?.name || '',
      segmentsCompleted: currentSegmentIndexRef.current,
      totalSegments: tourSegments?.length || 0,
    });

    // Reset state
    setPhase('aborted');
    setProgress(null);
    setCurrentSegmentIndex(0);
    currentSegmentIndexRef.current = 0;
    setTotalSegments(0);
    currentTourConfigRef.current = null;
    currentSegmentRef.current = null;

    // Reset to idle after a brief delay
    setTimeout(() => {
      setPhase('idle');
      isAbortingRef.current = false;
    }, 100);
  }, [stopLocalTour, cleanupCurrentSegment]);

  // Run a single segment
  const runSegment = useCallback(
    async (tourConfig: GlobalTourConfig, segmentIndex: number) => {
      if (isAbortingRef.current) return;

      const segment = tourConfig.segments[segmentIndex];
      if (!segment) {
        // Tour completed
        trackEvent(TRACKING_EVENTS.WELCOME_TOUR, {
          action: 'completed',
          totalDurationSeconds: 0,
          segmentsCompleted: tourConfig.segments.length,
          totalSegments: tourConfig.segments.length,
        });
        setPhase('completed');
        clearProgress();
        setSecureValue('sf-welcome-tour-completed', 'true');
        setTimeout(() => setPhase('idle'), 100);
        return;
      }

      currentSegmentRef.current = segment;
      setCurrentSegmentIndex(segmentIndex);
      currentSegmentIndexRef.current = segmentIndex;

      // Save progress
      const newProgress: GlobalTourProgress = {
        tourId: tourConfig.id,
        segmentIndex,
        startedAt: progress?.startedAt || Date.now(),
      };
      setProgress(newProgress);
      saveProgress(newProgress);

      // Phase 1: Inject demo data BEFORE navigation
      // Demo data must be in Redux/registry before the page mounts,
      // otherwise the page's useFocusEffect will fetch real data for
      // demo IDs and the reducer guards won't intercept (registry empty).
      // Skip inject if the previous segment already has identical demo data active
      // (e.g. Timeline → Batch General both use the same machine+batch demo data).
      const prevSegment = segmentIndex > 0 ? tourConfig.segments[segmentIndex - 1] : null;
      const reusesPrevDemoData = prevSegment?.demoData?.inject &&
        prevSegment.demoData.inject === segment.demoData?.inject;

      if (segment.demoData?.inject && !reusesPrevDemoData) {
        setPhase('injecting-data');
        try {
          await dispatch(segment.demoData.inject());
        } catch (error) {
          console.warn('Failed to inject demo data:', error);
        }
      }

      if (isAbortingRef.current) return;

      // Phase 2: Navigate if needed
      const currentRoute = navigationRef?.current?.getCurrentRoute()?.name;
      const needsNavigation = currentRoute !== segment.route;

      if (needsNavigation) {
        setPhase('navigating');

        // Resolve routeParams - can be object or function
        const params = typeof segment.routeParams === 'function'
          ? segment.routeParams()
          : (segment.routeParams || {});

        navigate(segment.route, params);

        // Wait a bit for navigation to start
        await delay(300);
      }

      if (isAbortingRef.current) return;

      // Phase 3: Wait for mount + settle
      setPhase('waiting-for-mount');
      const waitSelector = segment.waitForSelector || `[data-tour-id="${segment.steps[0]?.tourId}"]`;
      const waitTimeout = segment.waitTimeout || 5000;

      try {
        await waitForElement(waitSelector, waitTimeout);
      } catch (error) {
        console.warn(`Skipping segment "${segment.id}": ${error}`);
        // Cleanup demo data before skipping
        if (segment.demoData?.cleanup) {
          try { await dispatch(segment.demoData.cleanup()); } catch (_) {}
        }
        await runSegment(tourConfig, segmentIndex + 1);
        return;
      }

      if (isAbortingRef.current) return;

      // Allow extra settle time after mount for rendering to complete
      if (segment.demoData?.injectDelay) {
        await delay(segment.demoData.injectDelay);
      }

      if (isAbortingRef.current) return;

      // Phase 4: Drive the tour
      setPhase('driving');

      const isLastSegment = segmentIndex === tourConfig.segments.length - 1;

      // Build synthetic TourConfig for this segment
      const syntheticConfig: TourConfig = {
        id: `${tourConfig.id}-segment-${segmentIndex}`,
        name: segment.displayName || tourConfig.name,
        description: tourConfig.description,
        steps: segment.steps,
        driverConfig: {
          ...DEFAULT_DRIVER_CONFIG,
          ...tourConfig.driverConfig,
          ...segment.driverConfig,
          doneBtnText: isLastSegment ? 'Finish' : 'Continue →',
          onPopoverRender: (popover: PopoverDOM) => {
            // Inject progress indicator
            const progressEl = document.createElement('div');
            progressEl.className = 'sf-tour-global-progress';
            progressEl.innerHTML = `Area ${segmentIndex + 1} of ${tourConfig.segments.length}`;
            popover.title?.parentElement?.insertBefore(progressEl, popover.title);

            // Call original onPopoverRender if exists
            const originalConfig = segment.driverConfig || tourConfig.driverConfig;
            if (originalConfig?.onPopoverRender) {
              originalConfig.onPopoverRender(popover);
            }
          },
        },
      };

      // Handler for when segment completes
      const onSegmentComplete = async (closedByUser: boolean) => {
        if (closedByUser || isAbortingRef.current) {
          await stopGlobalTour();
          return;
        }

        // Skip cleanup if the next segment reuses the same demo data.
        // Cleaning up and re-injecting identical data creates a gap where
        // setTourActive(false) unblocks real API calls that overwrite demo state.
        const nextSegment = tourConfig.segments[segmentIndex + 1];
        const nextReusesDemoData = nextSegment?.demoData?.inject &&
          nextSegment.demoData.inject === segment.demoData?.inject;

        if (!nextReusesDemoData) {
          await cleanupCurrentSegment();
        }

        // Track segment completed (TODO: probably remove later - I think this will pollute events, but we will see after testing);
        trackEvent(TRACKING_EVENTS.WELCOME_TOUR, {
          action: 'segmentCompleted',
          segmentName: segment.name || '',
          segmentIndex,
        });

        // Transition to next segment
        setPhase('transitioning');

        // Run next segment
        await runSegment(tourConfig, segmentIndex + 1);
      };

      // Start the local tour for this segment
      startLocalTour(syntheticConfig, onSegmentComplete);
    },
    [dispatch, progress, startLocalTour, cleanupCurrentSegment, stopGlobalTour]
  );

  // Start a global tour
  const startGlobalTour = useCallback(
    async (tourId: string) => {
      if (!isWeb) return;

      const tourConfig = globalTourConfigs[tourId];
      if (!tourConfig) {
        console.warn(`Global tour config not found: ${tourId}`);
        return;
      }

      // Stop any running local tour
      if (isLocalTourActive) {
        stopLocalTour();
      }

      // Stop any running global tour
      if (isGlobalTourActive) {
        await stopGlobalTour();
      }

      isAbortingRef.current = false;
      currentTourConfigRef.current = tourConfig;
      setTotalSegments(tourConfig.segments.length);

      // Start from first segment
      await runSegment(tourConfig, 0);
    },
    [isLocalTourActive, isGlobalTourActive, stopLocalTour, stopGlobalTour, runSegment]
  );

  // Handle Escape key to abort tour
  useEffect(() => {
    if (!isWeb || !isGlobalTourActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        stopGlobalTour();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGlobalTourActive, stopGlobalTour]);

  // Resume tour from localStorage on mount
  useEffect(() => {
    if (!isWeb) return;

    const savedProgress = loadProgress();
    if (savedProgress) {
      const tourConfig = globalTourConfigs[savedProgress.tourId];
      if (tourConfig) {
        // Resume from saved progress
        trackEvent(TRACKING_EVENTS.WELCOME_TOUR, {
          action: 'resumed',
          segmentName: tourConfig.segments[savedProgress.segmentIndex]?.name || '',
        });
        currentTourConfigRef.current = tourConfig;
        setTotalSegments(tourConfig.segments.length);
        setProgress(savedProgress);
        runSegment(tourConfig, savedProgress.segmentIndex);
      } else {
        // Tour config no longer exists, clear progress
        clearProgress();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    startGlobalTour,
    stopGlobalTour,
    isGlobalTourActive,
    globalTourProgress: progress,
    globalTourPhase: phase,
    currentSegmentIndex,
    totalSegments,
  };
};
